import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Telescope, Search, BarChart3, ShieldCheck, Mail, Lock, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

function formatErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const { user, loading, login, loginEmail, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!loading && user) navigate("/dashboard", { replace: true }); }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "register") await register(form.email, form.password, form.name);
      else await loginEmail(form.email, form.password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Authentication failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0A0D14] text-slate-200">
      <div className="relative hidden lg:flex flex-col justify-between p-12 border-r border-slate-800/80 overflow-hidden">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1762117863408-8ab8d3493351?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400')", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14] via-[#0A0D14]/70 to-[#0A0D14]/40" />
        <div className="relative z-10 flex items-center gap-3">
          <Telescope className="w-7 h-7 text-amber-400" />
          <span className="font-serif text-2xl font-semibold tracking-tight">AuctionScope</span>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-serif text-4xl xl:text-5xl font-semibold leading-tight tracking-tight">Fine-art auction intelligence, normalized into one ledger.</h1>
          <p className="mt-5 text-sm leading-relaxed text-slate-400">Search, compare and export historical results from Bonhams, Christie's, Sotheby's and Phillips — London &amp; New York, GBP-normalized, under one canonical schema.</p>
          <Link to="/pricing" className="inline-block mt-6 text-sm text-amber-400 hover:text-amber-300 underline">See plans — from £0, up to 44% cheaper than Artprice →</Link>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-10"><Telescope className="w-7 h-7 text-amber-400" /><span className="font-serif text-2xl font-semibold">AuctionScope</span></div>
          <p className="text-[11px] font-mono uppercase tracking-widest text-amber-400/90">Research Terminal</p>
          <h2 className="mt-2 font-serif text-3xl font-semibold tracking-tight">{mode === "register" ? "Create your account" : "Sign in to continue"}</h2>

          <div className="mt-6 flex gap-1 p-1 al-card">
            <button data-testid="tab-login" onClick={() => setMode("login")} className={`flex-1 py-2 text-sm rounded-md transition-colors ${mode === "login" ? "bg-amber-500 text-[#0A0D14] font-medium" : "text-slate-400"}`}>Sign in</button>
            <button data-testid="tab-register" onClick={() => setMode("register")} className={`flex-1 py-2 text-sm rounded-md transition-colors ${mode === "register" ? "bg-amber-500 text-[#0A0D14] font-medium" : "text-slate-400"}`}>Create account</button>
          </div>

          <form onSubmit={submit} className="mt-5 space-y-3">
            {mode === "register" && (
              <div>
                <label className="al-label">Name</label>
                <div className="relative"><UserIcon className="w-4 h-4 absolute left-3 top-2.5 text-slate-500" /><input data-testid="auth-name-input" className="al-input pl-9" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" /></div>
              </div>
            )}
            <div>
              <label className="al-label">Email</label>
              <div className="relative"><Mail className="w-4 h-4 absolute left-3 top-2.5 text-slate-500" /><input data-testid="auth-email-input" type="email" required className="al-input pl-9" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" /></div>
            </div>
            <div>
              <label className="al-label">Password</label>
              <div className="relative"><Lock className="w-4 h-4 absolute left-3 top-2.5 text-slate-500" /><input data-testid="auth-password-input" type="password" required minLength={6} className="al-input pl-9" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" /></div>
            </div>
            <button data-testid="auth-submit-button" type="submit" disabled={busy} className="al-btn w-full py-2.5">{busy ? "Please wait…" : mode === "register" ? "Create account" : "Sign in"}</button>
          </form>

          <div className="flex items-center gap-3 my-5"><div className="flex-1 h-px bg-slate-800" /><span className="text-[10px] font-mono uppercase tracking-widest text-slate-600">or</span><div className="flex-1 h-px bg-slate-800" /></div>

          <button data-testid="google-login-button" onClick={login} className="al-btn-ghost w-full py-2.5">
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="currentColor" d="M12.24 10.4V14h5.03c-.22 1.17-.9 2.16-1.9 2.83v2.35h3.07C19.2 17.5 20 15 20 12.27c0-.64-.06-1.26-.17-1.86h-7.6z"/><path fill="currentColor" d="M12.24 20c2.43 0 4.47-.8 5.96-2.18l-3.07-2.35c-.85.57-1.94.9-2.89.9-2.22 0-4.1-1.5-4.77-3.52H4.3v2.42A8 8 0 0 0 12.24 20z"/></svg>
            Continue with Google
          </button>

          <div className="mt-8 space-y-2.5 text-sm text-slate-500">
            {[["Search & filter", Search], ["Cross-house compare", BarChart3], ["Role-based access", ShieldCheck]].map(([t, Icon]) => (
              <div key={t} className="flex items-center gap-3"><Icon className="w-4 h-4 text-amber-400/70" /> {t}</div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
